import React, { Fragment } from 'react';
import {
  useTranslate,
  NumberInput,
  TextInput,
  SelectInput,
  FormDataConsumer,
  required,
} from 'react-admin';
import { AmountInput } from '~/components/ra';
import lodashGet from 'lodash/get';
import { useForm } from 'react-final-form';

import { l } from '~/resources/pricings/PricingShow';
import { ByFeature } from '..';
import { AcquiringFeeType } from '~/api/pricings';
import { number } from 'react-admin';
import { minValue } from 'react-admin';

interface Props {
  getSource: (key: string) => string;
  schema: ByFeature;
  currency: string;
}

interface BaseFormProps extends Props {
  formData: any;
}

const BaseForm = (props: BaseFormProps) => {
  const { getSource, schema, formData, currency } = props;
  const scopedFormData = lodashGet(formData, getSource('')) || {};

  const translate = useTranslate();
  const t = (key: string): string => translate(l(key), 1);

  const form = useForm();

  // When adding a new acquiring fee type, keep this in sync with:
  //   1. The switch() cases rendered below (add the new case's UI)
  //   2. cleanup() in PricingDataEdit.tsx (add submit-time field removal for the new type)
  const handleTypeChange = (e: any) => {
    const newType = e.target.value as string | undefined;
    const clear = (fields: string[]) =>
      fields.forEach((field) => form.change(getSource(field), undefined));
    if (!newType) {
      clear(['amount', 'minAmount', 'maxAmount', 'percent']);
      return;
    }
    const typeLower = newType.toLowerCase();
    if (typeLower.includes('fixed')) {
      clear(['minAmount', 'maxAmount', 'percent']);
    } else if (typeLower.includes('percent')) {
      clear(['amount']);
    }
  };

  const currencyChange = (e: any) => {
    const name = e.target.name;
    const minAmountName = getSource('minAmount.currency');
    const maxAmountName = getSource('maxAmount.currency');
    const currency = lodashGet(form.getState().values, name);
    if (currency && name?.endsWith('.currency')) {
      if (name !== minAmountName) {
        form.change(minAmountName, currency);
      }
      if (name !== maxAmountName) {
        form.change(maxAmountName, currency);
      }
    }
  };

  return (
    <Fragment>
      <SelectInput
        source={getSource('type')}
        choices={schema['v:enum']}
        label={t('feeType')}
        allowEmpty
        emptyText='—'
        onChange={handleTypeChange}
      />
      {scopedFormData.type && (
        <TextInput source={getSource('name')} label={t('statementTitle')} validate={required()} />
      )}
      {(function () {
        switch (scopedFormData.type as AcquiringFeeType | undefined) {
          case 'FIXED_INCREASE':
          case 'FIXED_DECREASE':
            return (
              <AmountInput
                source={getSource('amount')}
                permanentSelectedCurrency={currency}
                validate={required()}
                fullWidth
              />
            );
          case 'PERCENT_INCREASE':
          case 'PERCENT_DECREASE':
            return (
              <Fragment>
                <NumberInput
                  source={getSource('percent')}
                  label={t('percent')}
                  validate={[number(), minValue(0), required()]}
                />
                <AmountInput
                  source={getSource('minAmount')}
                  amountLabel={t('minAmount')}
                  permanentSelectedCurrency={currency}
                  onChange={currencyChange}
                  fullWidth
                />
                <AmountInput
                  source={getSource('maxAmount')}
                  amountLabel={t('maxAmount')}
                  permanentSelectedCurrency={currency}
                  onChange={currencyChange}
                  fullWidth
                />
              </Fragment>
            );
          default:
            return null;
        }
      })()}
    </Fragment>
  );
};

const FeeForm = (props: Props) => {
  const { getSource, schema, currency } = props;

  return (
    <FormDataConsumer>
      {({ formData }: any) => {
        return (
          <Fragment>
            <BaseForm
              getSource={getSource}
              currency={currency}
              schema={schema}
              formData={formData}
            />
          </Fragment>
        );
      }}
    </FormDataConsumer>
  );
};

export default FeeForm;
